<template>
  <v-container fluid :class="containerClasses">
    <slot name="title"></slot>
    <v-layout row wrap :class="navigationClasses" justify-center v-if="$slots['navigation-bar']">
      <slot name="navigation-bar"></slot>
    </v-layout>
    <v-layout row wrap id="dataTable" :class="rowClasses">
      <v-flex xs12 class="table-responsive">
        <table class="table">
          <slot name="header" v-if="$slots.header"></slot>
          <tbody v-if="!items.length">
            <tr>
              <td class="text-xs-center" :colspan="tableHeader.length">
                <v-icon x-large class="grey--text text--lighten-1">info_outline</v-icon> No se encontraron resultados
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <slot></slot>
          </tbody>
        </table>
      </v-flex>
    </v-layout>
    <v-layout row wrap mt-4 justify-center v-if="meta.total > limit">
      <v-btn
        class="pagination-button"
        @click="goToPreviousPage"
        :disabled="currentPage <= 1"
        icon
        small>
        <v-icon pt-5>fa-caret-left</v-icon>
      </v-btn>
      <v-btn
        v-if="showLeftEllipsis"
        class="pagination-button"
        icon
        small
        disabled>...</v-btn>
      
      <v-btn
        class="paginate-button elevation-0"
        v-for="(item, index) in pagesToShow"
        :key="`pagination-item-${index}`"
        @click="goToPage(item)"
        :color="item === currentPage ? 'k-orange-linear' : 'default'"
        icon
        small>
        {{ item }}
      </v-btn>
      <v-btn
        v-if="showRightEllipsis"
        class="pagination-button"
        icon
        small
        disabled>...</v-btn>
      <v-btn
        class="pagination-button"
        @click="goToNextPage"
        :disabled="currentPage >= meta.last_page"
        icon
        small>
        <v-icon pt-5>fa-caret-right</v-icon>
      </v-btn>
    </v-layout>
  </v-container>
</template>

<script>

import _ from 'lodash';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
// import SkeletonLine from '@/components/Skeletons/Line';
// import SkeletonBox from '@/components/Skeletons/SkeletonBox';

/**
 * @group Global
 * <table>
 *  <tr>
 *    <th> Description </th>
 *  </tr>
 *  <tr>
 *    <td>
 *      Shows the elements of the parent
 *      component together with a control sidebar and makes the http
 *      request to the endpoint that is passed through the url property with the
 *      respective filters and get the necessary pages
 *    </td>
 *  </tr>
 * </table>
 * <br>
 * <h3> Components </h3>
 * <table>
 *  <tr>
 *    <th> Component </th>
 *    <th> Description </th>
 *  </tr>
 *  <tr>
 *    <td> VsPagination </td>
 *    <td> Makes the elments pagination </td>
 *  </tr>
 *  <tr>
 *    <td> VuePerfectScrollbar </td>
 *    <td> Render a scrolleable zone </td>
 *  </tr>
 *  </table>
 */
export default {
  components: {
    // SkeletonBox,
    // SkeletonLine,
    VuePerfectScrollbar,
  },
  props: {
    // The URL endpoint
    url: {
      type: String,
      default: '',
    },
    // The refresh boolean action
    refresh: {
      type: Boolean,
      required: false,
      // false
      default: false,
    },
    // The filters information for make the GET request
    filters: {
      type: Object,
      required: false,
      // {}
      default: () => ({}),
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    // The number limit of the items per request
    limit: {
      type: Number,
      required: false,
      // 10
      default: 10,
    },
    // The current page number
    page: {
      type: Number,
      required: false,
      // 1
      default: 1,
    },
    // Boolean for check if the content has header and set the scrollbar's height
    contentHeight: {
      type: String,
      required: false,
      default: '89%',
    },
    items: {
      type: Array,
      default: () => [],
    },
    tableHeader: {
      type: Array,
      default: () => [],
    },
    containerClasses: {
      type: Object,
      default: () => ({}),
    },
    navigationClasses: {
      type: Object,
      default: () => ({
        'mt-1 mb-3': true,
      }),
    },
    rowClasses: {
      type: Object,
      default: () => ({}),
    },
  },
  destroyed() {
    this.cancelToken.cancel('Canceled by user');
  },
  data() {
    return {
      currentPage: 1,
      isLoading: true,
      windowWidth: window.innerWidth,
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.30,
      },
      cancelToken: this.axios.CancelToken.source(),
    };
  },
  computed: {
    customStyle() {
      return {
        'max-height':` calc(${this.contentHeight} - 310px`,
      };
    },
    pagesToShow() {
      const totalPages = this.meta.last_page;
      const current = this.currentPage;
      let startPage = Math.max(1, current - 2);
      let endPage = Math.min(totalPages, current + 2);

      if (totalPages > 5) {
        if (current <= 3) {
          endPage = 5;
        } else if (current + 2 >= totalPages) {
          startPage = totalPages - 4;
        }
      }

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    },
    showLeftEllipsis() {
      return this.currentPage > 3 && this.meta.last_page > 5;
    },
    showRightEllipsis() {
      return this.currentPage < this.meta.last_page - 2 && this.meta.last_page > 5;
    }
  },
  methods: {
    // @vuese
    // Used to make the GET request to /url endpoint and get the elements for the given URL
    getItems() {
      if (this.url) {
        this.cancelToken.cancel('Canceled by user');
        this.cancelToken = this.axios.CancelToken.source();
        this.isLoading = true;
        this.axios
          .get(this.url, {
            params: {
              limit: this.limit,
              page: this.page,
              ...this.filters,
              ...(this.search && {
                search: this.search,
              }),
            },
            cancelToken: this.cancelToken.token,
          })
          .then(({ data }) => {
            // Fires when when the GET request is executed successfully and send the response data to the parent component
            // @arg The response data
            this.isLoading = false;
            this.$emit('items', data.items);
            this.$emit('full-data', data);
            this.$emit('meta', _.omit(data, ['items']));
          })
          .catch((error) => {
            if (
              error.message !== "Canceled by user" &&
              error.response.status !== 403
            ) {
              this.isLoading = false;
            }
          });
      } else {
        this.isLoading = false;
        this.$emit('search');
      }
    },
    goToPage(page) {
      this.currentPage = page;
      this.$emit('page', this.currentPage);
    },
    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        this.$emit('page', this.currentPage);
      }
    },
    goToNextPage() {
      if (this.currentPage < this.meta.last_page) {
        this.currentPage += 1;
        this.$emit('page', this.currentPage);
      }
    },
  },
  watch: {
    search: _.debounce(function() {
      this.$emit('meta', {
        pages: 1,
        page: 1,
      });
      this.getItems();
      this.$emit('refresh-search', this.search);
    }, 500),
    filters: {
      deep: true,
      handler() {
        this.$emit('meta', {
          pages: 1,
          page: 1,
        });
        this.getItems();
      },
    },
    refresh(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getItems();
      }
    },
    page(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentPage = newVal;
        this.getItems();
      }
    },
  },
}
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// src/components/global/DataTableV3/DataTableV3.vue